//////////////////
// IMPORTS
//////////////////
// schema
import { EcosystemEnum } from "@clockwise/schema";

// libraries
import * as React from "react";
import { connect } from "react-redux";
import { createFragmentContainer } from "react-relay";
// chrome-menu-drawer imports
import { chromeMenuDrawerFragments } from "./ChromeMenuDrawer.gql";
import { IContainer, IProps } from "./ChromeMenuDrawerTypes";
// other imports
import { ChromeDrawer } from "#webapp/src/components/chrome-drawer";
import { ChromeFeedback } from "#webapp/src/components/chrome-feedback";
import { LogoutBtn } from "#webapp/src/components/logout-btn";
// util
import { InviteModalMode } from "#webapp/src/components/invite-modal";
import { TrackingEvents } from "#webapp/src/util/analytics.util";
import { sortDataSources } from "#webapp/src/util/data-source.util";
import { filterAndSortCategories } from "#webapp/src/util/event-category-coloring.util";
import { getCurrentOrg } from "#webapp/src/util/org.util";
import { isPrimarySubscriptionFreePlan } from "#webapp/src/util/pricing.util";
import { maybeGetPrimaryTeamFromOrg } from "#webapp/src/util/team.util";
import { baseUrl } from "@clockwise/client-commons/src/constants/help-center";
import { routeToFeature } from "@clockwise/web-commons/src/util/routes.util";

// state
import {
  setActiveViewCalendarProfile,
  setMenuDrawerOpen,
  setOpenShareWithCoworkersModal,
} from "#webapp/src/state/actions/chrome-extension.actions";
import { setPricingDialogOpen } from "#webapp/src/state/actions/payments.actions";
import { IReduxState } from "#webapp/src/state/reducers/root.reducer";
// material-ui
import { AdminPanelSettingsIcon } from "#webapp/src/assets/img/icons";
import { getValue } from "#webapp/src/util/errorable.util";
import { getM365UrlWithRedirect } from "@clockwise/client-commons/src/config/api";
import {
  AddCircleOutline,
  ArrowUpward,
  Assistant,
  ExitToApp,
  GroupAdd,
  Help,
  Lightbulb,
  Link,
  People,
  Send,
  Settings,
} from "@clockwise/design-system/icons";
import { RestartAlt } from "@clockwise/icons";
import {
  appPaths,
  webappDefaultWindowName,
} from "@clockwise/web-commons/src/constants/route.constants";
import { DisabledWrapper } from "@clockwise/web-commons/src/ui/disabled-wrapper";
import { useIsOutlook } from "@clockwise/web-commons/src/util/OutlookContext";
import { useTracking } from "@clockwise/web-commons/src/util/analytics.util";
import { stringToCWId } from "@clockwise/web-commons/src/util/cw-id.util";
import { useEcosystem } from "@clockwise/web-commons/src/util/ecosystem";
import { getUrlForFeature } from "@clockwise/web-commons/src/util/routes.util";
import { Divider } from "@material-ui/core";
import { useCallback, useMemo } from "react";
import { useMonetization } from "../../hooks/useMonetization";
import { PostMessageManager } from "../../util/post-message.util";
import { ChromeMenuHeader } from "./ChromeMenuHeader";
import { ChromeMenuItem } from "./ChromeMenuItem";
import { CWID_PREFIX } from "./constants";

// List Item Text
const YOUR_IDEAL_DAY = "Your ideal day";
const YOUR_TEAMS = "Your teams";
const CREATE_A_TEAM = "Create or join a team";
const UPGRADE = "Upgrade";
const SHARE_WITH_COWORKERS = "Share with coworkers";
const SETTINGS = "Preferences";
const HELP_CENTER = "Help center";
const AI_SCHEDULER_BUTTON = "Reset AI Scheduler Button";
const ADMIN_PANEL = "Admin panel";
const SCHEDULING_LINKS = "Scheduling links";
const PERSONAL_CALENDAR = "Personal calendar";
const SLACK_INTEGRATION = "Slack integration";
const EVENT_COLORING = "Event color-coding";

const oneOnOneDisabledTooltip = {
  header: "Upgrade to manage your flexible meetings",
  body:
    "See all your 1:1s at a glance and let Clockwise know which meetings can be rescheduled to create Focus Time.",
};
//////////////////
// COMPONENT
//////////////////
export const ChromeMenuDrawerComponent = ({
  setMenuDrawerOpen,
  setActiveViewCalendarProfile,
  setPricingDialogOpen,
  setOpenShareWithCoworkersModal,
  windowUserEmail,
  isWebExtension,
  viewer,
  open,
  style,
}: IProps) => {
  const ecosystem = useEcosystem();
  const isOutlook = useIsOutlook();
  const isOutlookDesktop = isOutlook && !!window.Office.context.ui.openBrowserWindow;

  const { canUserAccessFlexMeetings, usersProductType } = useMonetization();
  const track = useTracking();
  const org = useMemo(() => getCurrentOrg(viewer), [viewer]);

  const closeDrawer = useCallback(() => {
    setMenuDrawerOpen(false);
  }, [setMenuDrawerOpen]);

  const onClickProfile = useCallback(() => {
    closeDrawer();
    setActiveViewCalendarProfile(windowUserEmail);
  }, [closeDrawer, setActiveViewCalendarProfile, windowUserEmail]);

  const onClickSlackIntegration = useCallback(() => {
    track(TrackingEvents.CHROME_MENU.CLICK_SLACK);
    if (isOutlookDesktop) {
      window.Office.context.ui.openBrowserWindow(getM365UrlWithRedirect(appPaths.appSettings));
    } else {
      window.open(`${appPaths.appSettings}`, webappDefaultWindowName);
    }
  }, [isOutlookDesktop, track]);

  const openUrl = useCallback(
    (url: string, target: string, urlOutlookDesktop?: string) => {
      if (isOutlookDesktop) {
        window.Office.context.ui.openBrowserWindow(urlOutlookDesktop || url);
      } else {
        window.open(url, target);
      }
    },
    [isOutlookDesktop],
  );

  // ~-~-~-~-~-~-~-
  // Render
  // ~-~-~-~-~-~-~-

  if (!org) {
    return <span />;
  }
  const showProBadge = usersProductType !== "Business" && usersProductType !== "Enterprise";

  // personal calendar
  const sortedDataSources = viewer.user.dataSources
    ? sortDataSources(viewer.user.dataSources.edges, org.domains)
    : null;
  const showPersonalCalendar = sortedDataSources && sortedDataSources.other.length === 0;
  // slack
  const slackAccounts = getValue(org.slackAccountsErrorable)?.list;
  const showSlack = slackAccounts && slackAccounts.length === 0;
  // color-coding
  const eventCategoryColorings = filterAndSortCategories(org.eventColoringSettingsErrorable);
  const showColorCoding = eventCategoryColorings.every((ecc) => !ecc.active);

  let person = getValue(org?.personForUserErrorable);
  // Schema types are incorrect and profile is actually nullable
  if (!person?.profile) {
    // If profile is missing, the person data is useless so treat it as null
    person = null;
  }
  const maybePrimaryTeamId = maybeGetPrimaryTeamFromOrg(org.primaryTeam)?.teamId;

  return (
    <ChromeDrawer style={style} open={open}>
      <ChromeMenuHeader
        person={person}
        email={windowUserEmail}
        onClose={closeDrawer}
        onAvatarClick={isWebExtension ? () => onClickProfile() : undefined}
      />
      <div className="cw-overflow-y-scroll">
        <ChromeMenuItem
          label={YOUR_IDEAL_DAY}
          onClick={() => {
            track(TrackingEvents.CHROME_MENU.CLICK_YOUR_IDEAL_DAY);
            routeToFeature({
              featureName: "accountPreferences",
              routerState: { kind: "window" },
              options: { isOutlookDesktop },
            });
          }}
          IconComponent={Lightbulb}
        />
        {maybePrimaryTeamId ? (
          <ChromeMenuItem
            label={YOUR_TEAMS}
            onClick={() => {
              track(TrackingEvents.CHROME_MENU.CLICK_YOUR_TEAMS);
              const teamUrl = `${appPaths.teamSettings}/${maybePrimaryTeamId}`;
              openUrl(teamUrl, webappDefaultWindowName, getM365UrlWithRedirect(teamUrl));
            }}
            IconComponent={People}
          />
        ) : (
          <ChromeMenuItem
            label={CREATE_A_TEAM}
            onClick={() => {
              track(TrackingEvents.CHROME_MENU.CLICK_TEAM_CREATE);
              openUrl(
                appPaths.createOrJoinTeam,
                webappDefaultWindowName,
                getM365UrlWithRedirect(appPaths.createOrJoinTeam),
              );
            }}
            IconComponent={GroupAdd}
          />
        )}
        {!isOutlook && (
          <ChromeMenuItem
            label={UPGRADE}
            onClick={() => {
              track(TrackingEvents.CHROME_MENU.CLICK_UPGRADE);
              setPricingDialogOpen(true, maybePrimaryTeamId);
            }}
            IconComponent={ArrowUpward}
            hide={!isPrimarySubscriptionFreePlan(org)}
          />
        )}
        {!isOutlook && (
          <ChromeMenuItem
            label={SHARE_WITH_COWORKERS}
            onClick={() => {
              track(TrackingEvents.CHROME_MENU.CLICK_INVITE);
              setOpenShareWithCoworkersModal(true, InviteModalMode.Invite);
            }}
            IconComponent={Send}
          />
        )}
        <DisabledWrapper
          isDisabled={!canUserAccessFlexMeetings}
          tooltipBody={oneOnOneDisabledTooltip.body}
          tooltipHeader={oneOnOneDisabledTooltip.header}
          width={260}
          tooltipStyles={{ left: "20px" }}
        >
          <ChromeMenuItem
            label="Flexible meetings"
            onClick={() => {
              track(TrackingEvents.CHROME_MENU.CLICK_AUTOPILOT);
              openUrl(
                appPaths.flexibleMeetings,
                webappDefaultWindowName,
                getM365UrlWithRedirect(appPaths.flexibleMeetings),
              );
            }}
            IconComponent={Assistant}
            showProBadge={showProBadge}
          />
        </DisabledWrapper>
        <ChromeMenuItem
          label={SCHEDULING_LINKS}
          onClick={() => {
            track(TrackingEvents.LINKS.LINK.MANAGE_LINKS);
            openUrl(
              appPaths.schedulingLinks,
              webappDefaultWindowName,
              getM365UrlWithRedirect(appPaths.schedulingLinks),
            );
          }}
          IconComponent={Link}
        />
        {(showPersonalCalendar || showSlack || showColorCoding) && (
          <>
            <Divider />
            <div className="cw-heading-base cw-mx-4 cw-mt-4 cw-mb-2">More features</div>
          </>
        )}
        <ChromeMenuItem
          label={PERSONAL_CALENDAR}
          onClick={() => {
            track(TrackingEvents.CHROME_MENU.CLICK_PERSONAL_CALENDAR);
            // Currently being hidden when Microsoft, but calling with openUrl to keep the same behavior for when we do enable for Microsoft.
            openUrl(
              appPaths.accountPreferences,
              webappDefaultWindowName,
              getM365UrlWithRedirect(appPaths.accountPreferences),
            );
          }}
          IconComponent={AddCircleOutline}
          hide={ecosystem === EcosystemEnum.Microsoft || !showPersonalCalendar}
        />
        <ChromeMenuItem
          label={SLACK_INTEGRATION}
          onClick={onClickSlackIntegration}
          IconComponent={AddCircleOutline}
          hide={!showSlack}
        />
        <ChromeMenuItem
          label={EVENT_COLORING}
          onClick={() => {
            track(TrackingEvents.CHROME_MENU.CLICK_COLORING);
            openUrl(
              getUrlForFeature("preferences", { hash: "#colorCoding" }),
              webappDefaultWindowName,
              getM365UrlWithRedirect(appPaths.accountPreferences),
            );
          }}
          IconComponent={AddCircleOutline}
          hide={!showColorCoding}
        />
        <Divider />
        <ChromeMenuItem
          label={SETTINGS}
          onClick={() => {
            track(TrackingEvents.CHROME_MENU.CLICK_SETTINGS);
            openUrl(
              appPaths.preferences,
              webappDefaultWindowName,
              getM365UrlWithRedirect(appPaths.preferences),
            );
          }}
          IconComponent={Settings}
        />
        <ChromeMenuItem
          label={ADMIN_PANEL}
          onClick={() => {
            track(TrackingEvents.CHROME_MENU.CLICK_ADMIN_PANEL);
            openUrl(
              appPaths.analytics,
              webappDefaultWindowName,
              getM365UrlWithRedirect(appPaths.analytics),
            );
          }}
          IconComponent={AdminPanelSettingsIcon}
          hide={ecosystem === EcosystemEnum.Microsoft}
        />
        <ChromeFeedback />
        <ChromeMenuItem
          label={HELP_CENTER}
          onClick={() => {
            track(TrackingEvents.CHROME_MENU.CLICK_HELP);
            openUrl(baseUrl, "_blank");
          }}
          IconComponent={Help}
        />
        <ChromeMenuItem
          label={AI_SCHEDULER_BUTTON}
          onClick={() => {
            PostMessageManager.sendResetScheduleWithAiButtonClosed();
          }}
          IconComponent={RestartAlt}
          hide={ecosystem === EcosystemEnum.Microsoft || isOutlook}
        />
        <LogoutBtn
          cw-id={stringToCWId("Sign out", CWID_PREFIX)}
          isMenuItem
          menuItemIcon={<ExitToApp className="cw-text-muted" />}
          onLogout={() => {
            track(TrackingEvents.CHROME_MENU.CLICK_LOGOUT);
            closeDrawer();
          }}
        />
      </div>
    </ChromeDrawer>
  );
};

//////////////////
// REDUX
//////////////////
function mapStateToProps(state: IReduxState, _ownProps: IContainer) {
  return {
    windowUserEmail: state.webExtension.windowUserEmail,
    isWebExtension: state.webExtension.isWebExtension,
  };
}

export const ChromeMenuDrawerRedux = connect(mapStateToProps, {
  setMenuDrawerOpen,
  setPricingDialogOpen,
  setOpenShareWithCoworkersModal,
  setActiveViewCalendarProfile,
})(ChromeMenuDrawerComponent);

//////////////////
// RELAY
//////////////////
export const ChromeMenuDrawer = createFragmentContainer<IContainer>(
  ChromeMenuDrawerRedux,
  chromeMenuDrawerFragments,
);
